/* Bireafon — Contact page behaviour.

   Three jobs, all progressive enhancement:
     1. Turn the always-open policy list into an accordion. The panels ship
        OPEN so a no-JS visitor still reads every answer; the script closes
        them and adds the toggling.
     2. Wire the Topic field to the accordion — picking a subject opens the
        answer to it, which is the actual ticket-deflection mechanism.
     3. Support-hours dot, and copy-to-clipboard on the support address.
*/
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- accordion */

  function setupAccordion(root) {
    var acc = root.querySelector('[data-bc-acc]');
    if (!acc) return null;

    var items = Array.prototype.slice.call(acc.querySelectorAll('[data-bc-item]'));
    if (!items.length) return null;

    // Take over: from here the panels are script-controlled.
    acc.setAttribute('data-js', '1');

    items.forEach(function (item, i) {
      var btn = item.querySelector('[data-bc-toggle]');
      var panel = item.querySelector('[data-bc-panel]');
      if (!btn || !panel) return;

      var open = i === 0 && item.getAttribute('data-open-first') === 'true';
      item.setAttribute('data-open', open ? 'true' : 'false');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');

      btn.addEventListener('click', function () {
        var isOpen = item.getAttribute('data-open') === 'true';
        setOpen(item, !isOpen);
      });
    });

    function setOpen(item, open) {
      var btn = item.querySelector('[data-bc-toggle]');
      item.setAttribute('data-open', open ? 'true' : 'false');
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function cue(key) {
      var match = null;
      items.forEach(function (item) {
        item.removeAttribute('data-cued');
        if (key && item.getAttribute('data-topic') === key) match = item;
      });
      if (!match) return null;

      setOpen(match, true);
      match.setAttribute('data-cued', 'true');
      window.setTimeout(function () {
        match.removeAttribute('data-cued');
      }, 2400);

      // Only pull the answer into view on narrow screens, where the accordion
      // sits below the form and would otherwise open off-screen.
      if (window.innerWidth < 990 && !REDUCED) {
        var box = match.getBoundingClientRect();
        if (box.top < 0 || box.bottom > window.innerHeight) {
          match.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return match;
    }

    return { items: items, setOpen: setOpen, cue: cue };
  }

  /* ------------------------------------------------------- topic → answer cue */

  function setupTopic(root, accordion) {
    var select = root.querySelector('[data-bc-topic]');
    if (!select || !accordion) return;

    select.addEventListener('change', function () {
      var opt = select.options[select.selectedIndex];
      var key = opt ? opt.getAttribute('data-key') : '';
      accordion.cue(key);
    });
  }

  /* -------------------------------------------- "that answered it" / "still stuck" */

  function setupResolve(root, accordion) {
    if (!accordion) return;

    var message = root.querySelector('[data-bc-message]');

    accordion.items.forEach(function (item) {
      var done = item.querySelector('[data-bc-resolved]');
      var stuck = item.querySelector('[data-bc-stuck]');

      if (done) {
        done.addEventListener('click', function () {
          item.setAttribute('data-resolved', 'true');
          accordion.setOpen(item, false);
        });
      }

      if (stuck) {
        stuck.addEventListener('click', function () {
          if (!message) return;
          message.focus();
          if (!REDUCED) {
            message.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    });
  }

  /* ------------------------------------------------------------ support hours */

  function setupHours(root) {
    var chip = root.querySelector('[data-bc-hours]');
    if (!chip) return;

    var label = chip.querySelector('[data-bc-hours-text]');
    var days = (chip.getAttribute('data-days') || '')
      .split(',')
      .map(function (d) { return parseInt(d, 10); })
      .filter(function (d) { return !isNaN(d); });
    var start = parseFloat(chip.getAttribute('data-start'));
    var end = parseFloat(chip.getAttribute('data-end'));
    var offset = parseFloat(chip.getAttribute('data-offset'));

    if (!days.length || isNaN(start) || isNaN(end) || isNaN(offset)) return;

    // Work in the support desk's own clock, derived from UTC so the visitor's
    // timezone never changes the answer.
    var now = new Date();
    var shifted = now.getUTCHours() + now.getUTCMinutes() / 60 + offset;
    var day = now.getUTCDay();

    if (shifted < 0) {
      shifted += 24;
      day = (day + 6) % 7;
    } else if (shifted >= 24) {
      shifted -= 24;
      day = (day + 1) % 7;
    }

    var open = days.indexOf(day) !== -1 && shifted >= start && shifted < end;

    chip.setAttribute('data-open', open ? 'true' : 'false');
    if (label) {
      var text = chip.getAttribute(open ? 'data-open-text' : 'data-closed-text');
      if (text) label.textContent = text;
    }
  }

  /* ------------------------------------------------------------------- copy */

  function setupCopy(root) {
    var buttons = root.querySelectorAll('[data-bc-copy]');
    // Only reveal the copy buttons if there is a clipboard to copy to.
    if (buttons.length && navigator.clipboard && navigator.clipboard.writeText) {
      root.setAttribute('data-bc-copy-ready', '1');
    }

    Array.prototype.forEach.call(buttons, function (btn) {
      var original = btn.textContent;
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        var value = btn.getAttribute('data-bc-copy');
        var doneLabel = btn.getAttribute('data-bc-copy-done') || 'Copied';

        var restore = function () {
          window.setTimeout(function () {
            btn.textContent = original;
          }, 1600);
        };

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(value).then(function () {
            btn.textContent = doneLabel;
            restore();
          }, function () {
            /* clipboard refused — the address is still visible beside the button */
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------- boot */

  function activate(root) {
    var accordion = setupAccordion(root);
    setupTopic(root, accordion);
    setupResolve(root, accordion);
    setupHours(root);
    setupCopy(root);
  }

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bc-root]'), function (root) {
      if (root.dataset.bcReady === '1') return;
      root.dataset.bcReady = '1';
      activate(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-bc-root]');
    if (root) {
      root.dataset.bcReady = '1';
      activate(root);
    }
  });
})();
